import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, Button, Paper, CircularProgress } from '@mui/material';
import { Lock as LockIcon, Person as UserIcon } from '@mui/icons-material';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Hardcoded credentials
  const ADMIN_USERNAME = 'NehaDeepak';
  const ADMIN_PASSWORD = 'NehaGharChaliGai';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Simulate authentication delay
    await new Promise(resolve => setTimeout(resolve, 500));

    if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
      // Store admin session
      localStorage.setItem('adminAuth', JSON.stringify({
        isAuthenticated: true,
        username: username,
        loginTime: new Date().toISOString()
      }));

      navigate('/admin');
    } else {
      setError('Invalid username or password');
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Paper
        elevation={10}
        sx={{
          padding: 4,
          borderRadius: 3,
          maxWidth: '450px',
          width: '100%',
          background: '#ffffff',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <Box mb={4} textAlign="center">
          <Box
            sx={{
              width: 80,
              height: 80,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              marginBottom: 2,
              color: 'white',
              fontSize: 40,
            }}
          >
            🔐
          </Box>
          <h1 style={{ margin: 0, color: '#1f2937', fontSize: '1.8rem', fontWeight: 800 }}>
            Admin Portal
          </h1>
          <p style={{ margin: '8px 0 0 0', color: '#6b7280', fontSize: '0.95rem' }}>
            Secure access for administrators
          </p>
        </Box>

        {error && (
          <Paper
            sx={{
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: 2,
              marginBottom: 3,
              color: '#991b1b',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <span>⚠️</span>
            <span>{error}</span>
          </Paper>
        )}

        <form onSubmit={handleSubmit}>
          {/* Username Field */}
          <Box mb={2.5}>
            <label
              style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 600,
                color: '#1f2937',
                fontSize: '0.95rem',
              }}
            >
              Username
            </label>
            <TextField
              fullWidth
              placeholder="Enter your username"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setError('');
              }}
              disabled={loading}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <UserIcon
                    sx={{
                      color: '#9ca3af',
                      marginRight: 1,
                      fontSize: '1.2rem',
                    }}
                  />
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: '#f3f4f6',
                  },
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  color: '#9ca3af',
                  opacity: 1,
                },
              }}
            />
          </Box>

          {/* Password Field */}
          <Box mb={3}>
            <label
              style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 600,
                color: '#1f2937',
                fontSize: '0.95rem',
              }}
            >
              Password
            </label>
            <TextField
              fullWidth
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              disabled={loading}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <LockIcon
                    sx={{
                      color: '#9ca3af',
                      marginRight: 1,
                      fontSize: '1.2rem',
                    }}
                  />
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: '#f3f4f6',
                  },
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  color: '#9ca3af',
                  opacity: 1,
                },
              }}
            />
          </Box>

          {/* Submit Button */}
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading || !username || !password}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '12px',
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              transition: '0.3s',
              '&:hover:not(:disabled)': {
                boxShadow: '0 10px 20px rgba(102, 126, 234, 0.3)',
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                background: '#d1d5db',
                cursor: 'not-allowed',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              <>🔓 Sign In</>
            )}
          </Button>
        </form>

        {/* Footer Info */}
        <Box
          mt={3}
          p={2}
          sx={{
            background: '#f3f4f6',
            borderRadius: '8px',
            textAlign: 'center',
            fontSize: '0.85rem',
            color: '#6b7280',
          }}
        >
          <p style={{ margin: 0, marginBottom: 8 }}>
            🔒 Secure Admin Access
          </p>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#9ca3af' }}>
            This portal is restricted to authorized administrators only.
          </p>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminLogin;
